import { useState, useMemo } from 'react';

/**
 * Custom hook for table sorting logic
 * Extracts duplicate sorting logic from RoundStandings and TeamStandings
 *
 * @param {string} initialSortField - Default field to sort by
 * @param {string} initialSortDirection - Default direction ('asc' or 'desc')
 * @param {Array} data - Array of data to sort
 * @param {string} stringField - Field name for string-based sorting (e.g., 'display_name', 'name')
 * @returns {Object} - { sortField, sortDirection, handleSort, sortedData, getSortIcon }
 */
export const useSortableTable = (
  initialSortField,
  initialSortDirection = 'desc',
  data = [],
  stringField = null
) => {
  const [sortField, setSortField] = useState(initialSortField);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default direction
      setSortField(field);
      // String fields default to 'asc', numeric fields default to 'desc'
      setSortDirection(field === stringField ? 'asc' : 'desc');
    }
  };

  // Memoize sorting to avoid unnecessary re-sorts
  // Only re-sort when data, sortField, or sortDirection change
  const sortedData = useMemo(() => {
    if (!data.length) return [];

    const sorted = [...data].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle string comparison for text fields
      if (sortField === stringField) {
        aVal = (aVal || '').toLowerCase();
        bVal = (bVal || '').toLowerCase();
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      // Numeric comparison
      aVal = aVal || 0;
      bVal = bVal || 0;

      if (sortDirection === 'asc') {
        return aVal - bVal;
      } else {
        return bVal - aVal;
      }
    });

    return sorted;
  }, [data, sortField, sortDirection, stringField]);

  const getSortIcon = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  return {
    sortField,
    sortDirection,
    handleSort,
    sortedData,
    getSortIcon,
  };
};
